// based on: https://github.com/apollographql/apollo-feature-requests/issues/162#issuecomment-830780576

import { useApolloClient } from '@apollo/client';
import getUuid from 'uuid-by-string';
import { useMemo, useState, useEffect, useContext } from 'react';
import { AppContext } from 'components/app-context';

function suspend(promise) {
  let status = 'pending';
  let response;

  const suspender = promise.then(
    res => {
      status = 'success';
      response = res;
    },
    err => {
      status = 'error';
      response = err;
    },
  );

  const read = () => {
    switch (status) {
      case 'pending':
        throw suspender;
      case 'error':
        return response;
      default:
        return response;
    }
  };
  return { read };
}

export function useSuspendableQuery(query, otherOptions) {
  const options = { query, ...otherOptions };
  const key = getUuid(
    `${query?.loc?.source?.body},${JSON.stringify(options?.variables)},${
      options?.uniqueKey
    }`,
  );

  const client = useApolloClient();

  const [updatedResult, setUpdatedResult] = useState();

  const { suspenseCache } = useContext(AppContext);

  const observableQuery = useMemo(() => {
    setUpdatedResult(undefined);
    return client.watchQuery(options);
  }, [key]);

  useEffect(() => {
    if (observableQuery) {
      const subscription = observableQuery.subscribe(
        setUpdatedResult,
        () => {},
      );
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [observableQuery]);

  const result = useMemo(() => {
    if (updatedResult) {
      return updatedResult;
    }

    if (!suspenseCache[key]) {
      suspenseCache[key] = {
        suspender: suspend(observableQuery.result()),
        options,
      };
    }

    return suspenseCache[key].suspender.read();
  }, [key, updatedResult]);

  return result;
}

export default useSuspendableQuery;
